import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import MobileDropdown from '@/components/MobileDropdown';
import { Save, Check, Store, Loader2, Bell, BellOff } from 'lucide-react';

const COUNTRIES = [
  { value: 'US', label: 'Estados Unidos (USD)' }, { value: 'MX', label: 'Mexico (MXN)' },
  { value: 'ES', label: 'Espana (EUR)' }, { value: 'CO', label: 'Colombia (COP)' },
  { value: 'AR', label: 'Argentina (ARS)' }, { value: 'CL', label: 'Chile (CLP)' },
  { value: 'PE', label: 'Peru (PEN)' }, { value: 'BR', label: 'Brasil (BRL)' },
  { value: 'EC', label: 'Ecuador (USD)' }, { value: 'PA', label: 'Panama (USD)' },
  { value: 'CR', label: 'Costa Rica (CRC)' }, { value: 'GT', label: 'Guatemala (GTQ)' },
  { value: 'DO', label: 'Rep. Dominicana (DOP)' }, { value: 'UY', label: 'Uruguay (UYU)' },
  { value: 'FR', label: 'Francia (EUR)' }, { value: 'IT', label: 'Italia (EUR)' },
  { value: 'DE', label: 'Alemania (EUR)' }, { value: 'GB', label: 'Reino Unido (GBP)' },
  { value: 'CA', label: 'Canada (CAD)' },
];

const RESTAURANT_TYPES = [
  { value: 'food_truck', label: 'Food Truck', desc: 'Costos bajos, alto volumen', mult: 'x2.0' },
  { value: 'casual', label: 'Casual Dining', desc: 'Restaurante informal', mult: 'x2.5' },
  { value: 'normal', label: 'Restaurante Normal', desc: 'Servicio completo estandar', mult: 'x3.0' },
  { value: 'upscale', label: 'Upscale', desc: 'Experiencia elevada', mult: 'x3.5' },
  { value: 'fine_dining', label: 'Fine Dining / Lujo', desc: 'Alta cocina, experiencia premium', mult: 'x4.5' },
];

export default function Settings() {
  const { user, updateProfile, token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState({ name: '', phone: '', country: 'US', restaurant_type: 'normal' });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recalcMsg, setRecalcMsg] = useState('');
  const [error, setError] = useState('');
  const [pushStatus, setPushStatus] = useState('unknown'); // unknown, granted, denied, subscribing, subscribed
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    // Check push notification status
    if ('Notification' in window) {
      setPushStatus(Notification.permission === 'granted' ? 'checking' : Notification.permission);
      if (Notification.permission === 'granted') {
        // Check if actually subscribed
        navigator.serviceWorker?.getRegistration('/push-sw.js').then(reg => {
          if (reg) {
            reg.pushManager.getSubscription().then(sub => {
              setPushStatus(sub ? 'subscribed' : 'granted');
            });
          } else {
            setPushStatus('granted');
          }
        }).catch(() => setPushStatus('granted'));
      }
    } else {
      setPushStatus('unsupported');
    }
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '', phone: user.phone || '',
        country: user.country || 'US', restaurant_type: user.restaurant_type || 'normal',
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setRecalcMsg(''); setError('');
    try {
      await updateProfile(form);
      const recalcRes = await fetch(`${API_URL}/api/recipes/recalculate`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (recalcRes.ok) {
        const recalcData = await recalcRes.json();
        setRecalcMsg(`${recalcData.message} (multiplicador: x${recalcData.multiplier})`);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (err) {
      setError('Error al guardar: ' + (err.response?.data?.detail || err.message || 'Error desconocido'));
    } finally { setLoading(false); }
  };

  const selectedType = RESTAURANT_TYPES.find(t => t.value === form.restaurant_type);
  const selectedCountry = COUNTRIES.find(c => c.value === form.country);

  return (
    <div className="space-y-8 animate-fade-in max-w-2xl" data-testid="settings-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Configuracion</p>
        <h2 className="font-heading text-3xl font-bold">Ajustes</h2>
      </div>
      <div className="bg-[#0A0A0A] border border-white/5 p-6 sm:p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 block">Email</label>
            <input value={user?.email || ''} disabled className="w-full bg-transparent border-b border-white/10 py-3 text-sm text-white/40 cursor-not-allowed" /></div>
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 block">Nombre</label>
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="settings-name-input" className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:border-gold focus:outline-none" /></div>
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 block">Telefono</label>
            <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} data-testid="settings-phone-input" className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:border-gold focus:outline-none" /></div>

          {/* PAIS - MobileDropdown custom component */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 block">Pais y Moneda</label>
            <MobileDropdown
              options={COUNTRIES}
              value={form.country}
              onChange={v => setForm(p => ({ ...p, country: v }))}
              testId="settings-country-select"
              placeholder="Seleccionar pais..."
            />
          </div>

          {/* TIPO RESTAURANTE - Radio buttons */}
          <div className="pt-4 border-t border-white/5">
            <div className="flex items-center gap-2 mb-4">
              <Store size={16} className="text-gold" />
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Tipo de Restaurante</label>
            </div>
            <p className="text-xs text-white/30 mb-4">Al guardar, todas tus recetas se recalculan automaticamente con el nuevo multiplicador.</p>
            <div className="grid grid-cols-1 gap-2">
              {RESTAURANT_TYPES.map(type => (
                <label key={type.value}
                  className={`flex items-center p-4 border cursor-pointer transition-all ${form.restaurant_type === type.value ? 'border-gold bg-gold/5' : 'border-white/10 hover:border-white/20'}`}
                  data-testid={`restaurant-type-${type.value}`}>
                  <input type="radio" name="restaurant_type" value={type.value}
                    checked={form.restaurant_type === type.value}
                    onChange={e => setForm(p => ({ ...p, restaurant_type: e.target.value }))}
                    className="w-4 h-4 accent-gold" />
                  <div className="flex-1 ml-3">
                    <p className={`text-sm font-medium ${form.restaurant_type === type.value ? 'text-gold' : 'text-white'}`}>{type.label}</p>
                    <p className="text-xs text-white/40">{type.desc}</p>
                  </div>
                  <span className={`text-xs font-mono px-2 py-1 ${form.restaurant_type === type.value ? 'bg-gold/20 text-gold' : 'bg-white/5 text-white/30'} hidden`}>{type.mult}</span>
                </label>
              ))}
            </div>
          </div>

          {error && <div className="bg-red-400/10 border border-red-400/30 p-3 text-red-400 text-xs" data-testid="settings-error">{error}</div>}
          {saved && recalcMsg && <div className="bg-emerald-400/10 border border-emerald-400/30 p-3 text-emerald-400 text-xs" data-testid="recalc-message">{recalcMsg}</div>}
          {saved && !recalcMsg && <div className="bg-emerald-400/10 border border-emerald-400/30 p-3 text-emerald-400 text-xs" data-testid="saved-message">Ajustes guardados correctamente</div>}

          <div className="flex items-center gap-4 pt-4">
            <button type="submit" disabled={loading} data-testid="save-settings-btn"
              className="flex items-center gap-2 px-6 py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors disabled:opacity-50">
              {loading ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : <Save size={14} />}
              {saved ? 'GUARDADO' : loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>

      {/* Push Notifications Section */}
      <div className="bg-[#0A0A0A] border border-white/5 p-6 sm:p-8" data-testid="push-notifications-section">
        <div className="flex items-center gap-2 mb-4">
          <Bell size={16} className="text-gold" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Notificaciones Push</p>
        </div>
        <p className="text-xs text-white/30 mb-4">Recibe alertas criticas directamente en tu dispositivo, incluso cuando no tienes la app abierta.</p>
        
        {pushStatus === 'unsupported' && (
          <div className="p-3 bg-white/5 border border-white/10 text-xs text-white/40">Tu navegador no soporta notificaciones push.</div>
        )}
        
        {pushStatus === 'denied' && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 text-xs text-red-400">
            Las notificaciones estan bloqueadas. Ve a los ajustes de tu navegador para habilitarlas.
          </div>
        )}
        
        {(pushStatus === 'default' || pushStatus === 'granted' || pushStatus === 'unknown' || pushStatus === 'checking') && (
          <button 
            onClick={async () => {
              setPushLoading(true);
              try {
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') { setPushStatus('denied'); return; }
                const reg = await navigator.serviceWorker.register('/push-sw.js');
                await reg.update();
                const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
                const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
                const rawKey = Uint8Array.from(atob(vapidKey.replace(/-/g,'+').replace(/_/g,'/') + padding), c => c.charCodeAt(0));
                const subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: rawKey });
                await fetch(`${API_URL}/api/push/subscribe`, {
                  method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ subscription: subscription.toJSON() })
                });
                setPushStatus('subscribed');
                await fetch(`${API_URL}/api/push/test`, {
                  method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                });
              } catch (err) { console.error('Push subscribe error:', err); alert('Error al activar notificaciones: ' + err.message); }
              finally { setPushLoading(false); }
            }}
            disabled={pushLoading}
            data-testid="enable-push-btn"
            className="flex items-center gap-2 px-6 py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors disabled:opacity-50"
          >
            {pushLoading ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
            Activar Notificaciones
          </button>
        )}

        {pushStatus === 'subscribed' && (
          <div>
            <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs mb-4">
              <Check size={14} /> Notificaciones push activadas
            </div>
            <button 
              onClick={async () => {
                setPushLoading(true);
                try {
                  const reg = await navigator.serviceWorker.getRegistration('/push-sw.js');
                  if (reg) { const sub = await reg.pushManager.getSubscription(); if (sub) await sub.unsubscribe(); }
                  await fetch(`${API_URL}/api/push/unsubscribe`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
                  setPushStatus('granted');
                } catch (err) { console.error(err); }
                finally { setPushLoading(false); }
              }}
              disabled={pushLoading}
              data-testid="disable-push-btn"
              className="flex items-center gap-2 px-4 py-2 border border-white/10 text-white/40 text-xs hover:text-red-400 hover:border-red-400/30 transition-colors disabled:opacity-50"
            >
              <BellOff size={14} /> Desactivar
            </button>
          </div>
        )}
      </div>

      <div className="bg-[#0A0A0A] border border-white/5 p-6 sm:p-8">
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-4">Informacion de cuenta</p>
        <div className="space-y-3">
          <div className="flex justify-between text-sm"><span className="text-white/40">Rol</span><span className="text-gold uppercase tracking-widest text-xs">{user?.role}</span></div>
          <div className="flex justify-between text-sm"><span className="text-white/40">Moneda</span><span>{selectedCountry?.label?.split('(')[1]?.replace(')','') || user?.currency}</span></div>
          <div className="flex justify-between text-sm"><span className="text-white/40">Plan</span><span className={user?.subscription_status === 'active' ? 'text-gold' : 'text-white/40'}>{user?.subscription_status === 'active' ? 'Professional' : 'Prueba'}</span></div>
          <div className="flex justify-between text-sm"><span className="text-white/40">Tipo Restaurante</span><span className="text-gold">{selectedType?.label}</span></div>
        </div>
      </div>
      <p className="text-[9px] text-white/10 text-center" data-testid="version-indicator">v2026.03.06b</p>
    </div>
  );
}
